/**
 * @module Accordion
 * Manages accessible accordion components, typically for mobile views of tab layouts.
 */
class Accordion {
    /**
     * @param {HTMLElement} element - The main container for the accordion.
     */
    constructor(element) {
        this.container = element;
        this.items = Array.from(this.container.querySelectorAll('.accordion-item'));
        // In this context, only one panel should be open at a time.
        this.allowMultiple = false;

        if (this.items.length === 0) {
            console.error('Accordion component is missing required .accordion-item elements:', this.container);
            return;
        }

        this.prepareInitialState();
        this.bindEvents();
    }

    /**
     * Sets the initial max-height for panels for smooth transitions.
     */
    prepareInitialState() {
        this.items.forEach(item => {
            const panel = item.querySelector('.accordion-item__panel');
            if (!panel) return;

            if (item.classList.contains('is-active')) {
                panel.style.maxHeight = 'none';
            } else {
                panel.style.maxHeight = '0px';
            }
        });
    }

    /**
     * Binds click events to accordion headers using event delegation.
     */
    bindEvents() {
        this.container.addEventListener('click', (event) => {
            const button = event.target.closest('.accordion-item__button');
            if (button) {
                this.handleClick(button);
            }
        });
    }

    /**
     * Handles click events on an accordion button.
     * @param {HTMLButtonElement} clickedButton - The button that was clicked.
     */
    handleClick(clickedButton) {
        const parentItem = clickedButton.closest('.accordion-item');
        const isExpanded = clickedButton.getAttribute('aria-expanded') === 'true';

        if (!this.allowMultiple) {
            // Close other items before opening the new one
            this.items.forEach(item => {
                if (item !== parentItem) {
                    this.collapseItem(item);
                }
            });
        }

        if (!isExpanded) {
            this.expandItem(parentItem);
        } else {
            this.collapseItem(parentItem);
        }
    }

    /**
     * Expands an accordion item.
     * @param {HTMLElement} item - The accordion item container.
     */
    expandItem(item) {
        const button = item.querySelector('.accordion-item__button');
        const panel = item.querySelector('.accordion-item__panel');
        if (!button || !panel) return;

        item.classList.add('is-active');
        button.setAttribute('aria-expanded', 'true');
        panel.hidden = false;

        const contentHeight = panel.scrollHeight;
        panel.style.maxHeight = `${contentHeight}px`;

        panel.addEventListener('transitionend', function onTransitionEnd(e) {
            if (e.target !== panel) return;
            panel.removeEventListener('transitionend', onTransitionEnd);
            if (item.classList.contains('is-active')) {
                panel.style.maxHeight = 'none';
            }
        });
    }

    /**
     * Collapses an accordion item.
     * @param {HTMLElement} item - The accordion item container.
     */
    collapseItem(item) {
        const button = item.querySelector('.accordion-item__button');
        const panel = item.querySelector('.accordion-item__panel');
        if (!button || !panel || !item.classList.contains('is-active')) return;

        panel.style.maxHeight = `${panel.scrollHeight}px`;

        requestAnimationFrame(() => {
            item.classList.remove('is-active');
            button.setAttribute('aria-expanded', 'false');
            panel.style.maxHeight = '0px';
        });

        panel.addEventListener('transitionend', function onTransitionEnd(e) {
            if (e.target !== panel) return;
            panel.removeEventListener('transitionend', onTransitionEnd);
            if (!item.classList.contains('is-active')) {
                panel.hidden = true;
            }
        });
    }
}

/**
 * Initializes all accordion components on the page.
 */
export function initializeAccordions() {
    const accordions = document.querySelectorAll('[data-accordion-component]');
    accordions.forEach(accordion => {
        if (!accordion.dataset.accordionInitialized) {
            new Accordion(accordion);
            accordion.dataset.accordionInitialized = 'true';
        }
    });
}